import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

// تعريف واجهة قيم الفلتر
interface FilterValues {
  barcode: string;
  productName: string;
  category: string;
  warehouse: string;
}

// تعريف واجهة الخصائص
interface InventoryReportFilterProps {
  filterValues: FilterValues;
  warehouses: string[];
  categories: { id: string; name: string }[];
  onFilterChange: (newValues: FilterValues) => void;
}

// مكون فلتر تقرير المخزون
const InventoryReportFilter: React.FC<InventoryReportFilterProps> = ({
  filterValues,
  warehouses,
  categories,
  onFilterChange
}) => {
  // حالة الفلتر المحلية
  const [localFilter, setLocalFilter] = useState<FilterValues>(filterValues);

  // معالجة تغيير قيم الفلتر المحلية
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLocalFilter(prev => ({ ...prev, [name]: value }));
  };

  // تطبيق الفلتر
  const applyFilter = () => {
    onFilterChange(localFilter);
  };

  // إعادة تعيين الفلتر
  const resetFilter = () => {
    const emptyFilter: FilterValues = {
      barcode: '',
      productName: '',
      category: '',
      warehouse: ''
    };
    setLocalFilter(emptyFilter);
    onFilterChange(emptyFilter);
  };

  return (
    <Box sx={{ my: 2 }}>
      <Grid container spacing={2}>
        {/* حقل الباركود */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="الباركود"
            name="barcode"
            value={localFilter.barcode}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: localFilter.barcode ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalFilter(prev => ({ ...prev, barcode: '' }));
                  }}
                  edge="start"
                  sx={{ ml: -1, mr: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
        </Grid>

        {/* حقل اسم المنتج */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="اسم المنتج"
            name="productName"
            value={localFilter.productName}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: localFilter.productName ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalFilter(prev => ({ ...prev, productName: '' }));
                  }}
                  edge="start"
                  sx={{ ml: -1, mr: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
        </Grid>

        {/* حقل التصنيف */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="التصنيف"
            name="category"
            value={localFilter.category}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">
              <em>الكل</em>
            </MenuItem>
            {categories.map(category => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* حقل المستودع */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="المستودع"
            name="warehouse"
            value={localFilter.warehouse}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">
              <em>الكل</em>
            </MenuItem>
            {warehouses.map(warehouse => (
              <MenuItem key={warehouse} value={warehouse}>
                {warehouse}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={resetFilter}
          sx={{ mx: 1 }}
        >
          إعادة تعيين
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={applyFilter}
          startIcon={<SearchIcon />}
          sx={{
            bgcolor: '#30c3b0',
            '&:hover': { bgcolor: '#25a899' },
            boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
          }}
        >
          بحث
        </Button>
      </Box>
    </Box>
  );
};

export default InventoryReportFilter;